import {
  Component,
  Suspense,
  lazy,
  type ComponentType,
  type CSSProperties,
  type ErrorInfo,
  type LazyExoticComponent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { reportClientError } from './clientErrors.js';

// UX-001: the app had NO client-side error handling — a render throw took down the whole shell (white
// screen), all 15 lazy views used `fallback={null}` (a deploy that invalidates hash-named chunks left
// an open tab staring at a blank pane), and nothing was recorded. This module supplies the three
// missing pieces: a render ErrorBoundary with a themed recovery card, a minimal Suspense spinner, and
// a lazy() wrapper that retries a stale chunk once before surfacing to the nearest boundary.

const cardStyle: CSSProperties = {
  margin: '24px auto',
  maxWidth: 420,
  padding: '20px 24px',
  border: '1px solid var(--lapo-border)',
  borderRadius: 10,
  background: 'var(--lapo-bg)',
  color: 'var(--lapo-fg)',
  font: '14px/1.5 system-ui, sans-serif',
  textAlign: 'center',
};
const buttonStyle: CSSProperties = {
  marginTop: 16,
  padding: '8px 18px',
  border: 'none',
  borderRadius: 8,
  background: 'var(--lapo-accent)',
  color: '#fff',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

/** The recovery UI shown in place of a subtree that threw. Reload re-fetches index.html + fresh chunks. */
function ErrorCard({ onReload }: { onReload: () => void }): ReactElement {
  return (
    <div role="alert" style={cardStyle}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Something went wrong here.</div>
      <div style={{ color: 'var(--lapo-muted)' }}>
        This part of Lapo hit an error. Reloading usually fixes it.
      </div>
      <button type="button" style={buttonStyle} onClick={onReload}>
        Reload
      </button>
    </div>
  );
}

/** Minimal, unobtrusive Suspense fallback while a lazy view/editor chunk loads (replaces `null`). */
export function ViewSpinner(): ReactElement {
  return (
    <div
      aria-busy="true"
      aria-label="Loading"
      style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 32, color: 'var(--lapo-faint)' }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          border: '2px solid var(--lapo-border)',
          borderTopColor: 'var(--lapo-accent)',
          borderRadius: '50%',
          animation: 'lapo-td-spin 0.7s linear infinite',
        }}
      />
    </div>
  );
}

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional label folded into the crash report to identify the wrapped surface. */
  label?: string;
}
interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * A render ErrorBoundary: catches a throw in its subtree, reports it (UX-001 forensics), and shows a
 * recovery card instead of letting the exception unmount the whole React tree. Used both as the app's
 * outermost backstop (main.tsx) and around the content-area view switch so a crashing view can't take
 * the sidebar/nav down with it.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    const source = [this.props.label, info.componentStack?.trim()].filter(Boolean).join(' ');
    reportClientError('render', error, source || undefined);
  }

  private readonly reload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.error) return <ErrorCard onReload={this.reload} />;
    return this.props.children;
  }
}

/**
 * Wrap a lazy view so it (a) shows the spinner while its chunk loads and (b) shows the recovery card —
 * not a blank pane — if the chunk fails to load or the view throws. Editors are wrapped too: the
 * boundary only swaps to the card ON a throw, so a healthy Y.Doc-bound editor renders untouched.
 */
export function LazyBoundary({ children }: { children: ReactNode }): ReactElement {
  return (
    <ErrorBoundary>
      <Suspense fallback={<ViewSpinner />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

/**
 * lazy() with one retry. A deploy invalidates hash-named chunks, so an open tab importing a not-yet-
 * loaded view can 404 on the old chunk name. Retry once (a fresh network fetch); if it still fails the
 * rejection surfaces to the nearest LazyBoundary, whose Reload re-fetches index.html + the new names.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React's own lazy() signature so
// any component (whatever its props) can be wrapped while callers keep full ComponentProps<> inference.
export function lazyView<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await load();
    } catch {
      try {
        return await load();
      } catch (err) {
        reportClientError('chunk-load', err);
        throw err;
      }
    }
  });
}
